// ---------- technique icon ----------
// A tiny illustration of a puzzle's rendering family — shapes, hatching, ASCII, stippling,
// dice — built from each family's REAL geometry (shared/alphabets/*.ts: half-cells and
// diagonal corners for the shape alphabets, parallel/crossed strokes for hachuresPleines, the
// actual ramp characters for asciiElargi, pips for desPlein), not an arbitrary pictogram.
// Designed and visually checked in the artifact "Icônes des techniques" before landing here.
//
// Five families, not nine ids, EXCEPT for motifs/motifsFin/motifsTrait: the Alphabet filter
// (home.ts::alphaTile()) shows a full-size illustration per value with its exact name right
// below it, and the three motifs variants looking identical there reads as a bug, not as "close
// enough" — so they get their own marks (CELLS_BY_ID below) while pointille/pointilleElargi/
// pointilleRiche still share theirs (only asked to fix the former, and a family read remains
// fine where nothing else distinguishes them, e.g. a library card corner with no label at all).

import { t } from './i18n';

const FAMILY_OF: Record<string, TechFamily> = {
  motifs: 'motifs',
  motifsFin: 'motifs',
  motifsTrait: 'motifs',
  hachuresPleines: 'hachures',
  asciiElargi: 'ascii',
  pointille: 'pointille',
  pointilleElargi: 'pointille',
  pointilleRiche: 'pointille',
  desPlein: 'des',
  briques: 'briques',
  peinture: 'peinture',
  peintureNuancee: 'peinture',
  briquesNuancees: 'briques',
  pointDeCroix: 'croix',
};

export type TechFamily =
  'motifs' | 'hachures' | 'ascii' | 'pointille' | 'des' | 'briques' | 'peinture' | 'croix';

/** Retombe sur `motifs` pour un id que ce registre ne connaît pas encore (un alphabet ajouté
 *  côté admin avant que le jeu ne sache l'illustrer) — jamais une icône vide. */
export function techniqueFamily(alphabetId: string): TechFamily {
  return FAMILY_OF[alphabetId] ?? 'motifs';
}

/** Nom générique d'une famille (pas d'un alphabetId précis) — utilisé là où plusieurs alphabetId
 *  d'une même famille sont regroupés sous une seule illustration (cf. src/packs.ts) et où le nom
 *  exact de chacun n'a plus de sens à afficher. */
export function techniqueFamilyLabel(fam: TechFamily): string {
  return t(`technique.family.${fam}`);
}

// Les 4 marques représentatives par famille, que techniqueIconLg() joint en grille 2×2 (coin de
// card, Bibliothèque, fiche d'un pack).
const CELLS: Record<TechFamily, string[]> = {
  motifs: [
    `<i class="tc"></i>`,
    `<i class="tc tc-half-bottom"></i>`,
    `<i class="tc tc-diag-br"></i>`,
    `<i class="tc tc-half-left"></i>`,
  ],
  hachures: [
    `<i class="tc tc-hach-a"></i>`,
    `<i class="tc tc-hach-b"></i>`,
    `<i class="tc tc-hach-c"></i>`,
    `<i class="tc tc-hach-d"></i>`,
  ],
  ascii: [
    `<i class="tc tc-ch">.</i>`,
    `<i class="tc tc-ch">+</i>`,
    `<i class="tc tc-ch">@</i>`,
    `<i class="tc tc-ch">█</i>`,
  ],
  pointille: [
    `<i class="tc tc-dot tc-dot-a"></i>`,
    `<i class="tc tc-dot tc-dot-b"></i>`,
    `<i class="tc tc-dot tc-dot-c"></i>`,
    `<i class="tc tc-dot tc-dot-d"></i>`,
  ],
  des: [
    `<i class="tc tc-die tc-die-1"><b></b></i>`,
    `<i class="tc tc-die tc-die-2"><b></b><b></b></i>`,
    `<i class="tc tc-die tc-die-4"><b></b><b></b><b></b><b></b></i>`,
    `<i class="tc tc-die tc-die-6"><b></b><b></b><b></b><b></b><b></b><b></b></i>`,
  ],
  // Quatre briques de quatre couleurs franches de la palette (shared/alphabets/briques.ts) :
  // c'est la couleur, plus que le tenon, qui dit « mosaïque » à 22 px.
  briques: [
    `<i class="tc tc-brick tc-brick-a"></i>`,
    `<i class="tc tc-brick tc-brick-b"></i>`,
    `<i class="tc tc-brick tc-brick-c"></i>`,
    `<i class="tc tc-brick tc-brick-d"></i>`,
  ],
  // Quatre touches couchées dans des sens différents, de quatre couleurs de la palette
  // (shared/alphabets/peinture.ts) : c'est l'inclinaison, plus que la couleur, qui dit « pinceau ».
  peinture: [
    `<i class="tc tc-paint tc-paint-a"></i>`,
    `<i class="tc tc-paint tc-paint-b"></i>`,
    `<i class="tc tc-paint tc-paint-c"></i>`,
    `<i class="tc tc-paint tc-paint-d"></i>`,
  ],
  // Quatre croix de fil de quatre couleurs du nuancier (shared/alphabets/pointDeCroix.ts) : le X
  // seul dit « point de croix », même à 22 px (2026-10-22).
  croix: [
    `<i class="tc tc-cross tc-cross-a"></i>`,
    `<i class="tc tc-cross tc-cross-b"></i>`,
    `<i class="tc tc-cross tc-cross-c"></i>`,
    `<i class="tc tc-cross tc-cross-d"></i>`,
  ],
};

// Les deux variantes de motifs.ts que la famille 'motifs' ne distingue pas : motifsFin.ts ajoute
// des paliers 1/4 et 3/4 (un remplissage plus graduel), motifsTrait.ts remplace la demi-case par
// un trait centré. `motifs` lui-même n'a pas besoin d'entrée ici — CELLS.motifs est déjà le bon
// jeu de marques pour lui.
const CELLS_BY_ID: Partial<Record<string, string[]>> = {
  motifsFin: [
    `<i class="tc tc-quart-bottom"></i>`,
    `<i class="tc tc-half-bottom"></i>`,
    `<i class="tc tc-threequart-bottom"></i>`,
    `<i class="tc"></i>`,
  ],
  motifsTrait: [
    `<i class="tc tc-half-bottom"></i>`,
    `<i class="tc tc-trait tc-trait-v"><i></i></i>`,
    `<i class="tc tc-trait tc-trait-h"><i></i></i>`,
    `<i class="tc tc-trait tc-trait-d"><i></i></i>`,
  ],
};

function cellsFor(alphabetId: string): string[] {
  return CELLS_BY_ID[alphabetId] ?? CELLS[techniqueFamily(alphabetId)];
}

/**
 * La grande illustration — coin de carte de bibliothèque à la place du « ? » d'un puzzle non
 * commencé (cf. buildCard() dans home.ts), et tuile du filtre Alphabet (cf. alphaTile() dans
 * home.ts). Quatre cases représentatives en grille 2×2.
 */
export function techniqueIconLg(alphabetId: string): string {
  const fam = techniqueFamily(alphabetId);
  return `<span class="tech-icon tech-lg tech-${fam}">${cellsFor(alphabetId).join('\n')}</span>`;
}

const CELLS_SM: Record<TechFamily, string> = {
  motifs: `<i class="tc tc-diag-br"></i>`,
  hachures: `<i class="tc tc-hach-c"></i>`,
  ascii: `<i class="tc tc-ch">@</i>`,
  pointille: `<i class="tc tc-dot tc-dot-b"></i>`,
  des: `<i class="tc tc-die tc-die-4"><b></b><b></b><b></b><b></b></i>`,
  briques: `<i class="tc tc-brick tc-brick-a"></i>`,
  peinture: `<i class="tc tc-paint tc-paint-a"></i>`,
  croix: `<i class="tc tc-cross tc-cross-a"></i>`,
};

/**
 * La vignette « Vitrine » d'une card de pack multi-technique (cf. src/packs.ts) : une par
 * famille présente dans le pack, teintée à la couleur de la famille (cf. .tint-<fam> dans
 * style.css), avec le nombre de puzzles en badge et son nom en légende — un présentoir, pas
 * juste un décompte. Une seule marque représentative (CELLS_SM), à une taille intermédiaire
 * entre la puce d'origine que ce jeu de données servait et la card de bibliothèque.
 */
export function techniqueIconSwatch(alphabetId: string, count: number): string {
  const fam = techniqueFamily(alphabetId);
  // La teinte va sur la tuile elle-même (le <i class="tc">, fond papier) — pas sur le <span>
  // .tech-icon qui l'englobe, qui n'a pas de fond propre — d'où l'injection dans la classe de
  // CELLS_SM plutôt qu'une classe posée à côté, qui n'aurait rien eu à teindre.
  const cell = CELLS_SM[fam].replace('class="tc', `class="tc tint-${fam}`);
  return `<span class="tech-fam">
    <span class="tech-fam-icon">
      <span class="tech-icon tech-md tech-${fam}">${cell}</span>
      <b class="tech-fam-n">${count}</b>
    </span>
    <span class="tech-fam-label">${techniqueFamilyLabel(fam)}</span>
  </span>`;
}
